'use strict';

// Board markup — a deliberately tiny, SAFE markdown-lite for board posts and
// comments (CJ 2026-07-31). Users never write raw HTML; we escape everything
// first, then apply a fixed set of transforms. The SERVER renders body_html at
// save time (the stored, trusted copy the wall + email use); the CLIENT uses the
// same function for the compose preview so what you see is what posts.
//
// Supported:
//   # Heading            → <h4>            (single level; keeps posts flat)
//   **bold**             → <b>
//   *italic*             → <i>
//   [text](https://url)  → <a> (http/https/mailto only; opens in a new tab)
//   - bullet             → <ul><li>
//   blank line           → new <p>; single newline → <br>
// Anything else is literal text. No images, no raw tags, no javascript: URLs.

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function safeUrl(u) {
  const t = String(u || '').trim();
  return /^(https?:\/\/|mailto:)/i.test(t) ? t : null; // reject javascript:, data:, etc.
}

// Inline transforms applied to already-escaped text.
function inline(escaped) {
  let s = escaped;
  // links: [text](url) — url captured from the ESCAPED string, so validate/rebuild
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, text, rawUrl) => {
    const url = safeUrl(rawUrl.replace(/&amp;/g, '&')); // undo escape for the href check
    if (!url) return text;                              // drop the link, keep the text
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });
  s = s.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
  s = s.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<i>$2</i>');
  return s;
}

function renderBoardMarkup(md) {
  const src = String(md == null ? '' : md).replace(/\r\n/g, '\n').trim();
  if (!src) return '';
  const blocks = src.split(/\n{2,}/);
  const out = [];
  for (const block of blocks) {
    const lines = block.split('\n');
    // A block of bullet lines → a list.
    if (lines.every((l) => /^\s*-\s+/.test(l))) {
      const items = lines.map((l) => `<li>${inline(escapeHtml(l.replace(/^\s*-\s+/, '')))}</li>`).join('');
      out.push(`<ul>${items}</ul>`);
      continue;
    }
    // A single heading line.
    if (lines.length === 1 && /^#\s+/.test(lines[0])) {
      out.push(`<h4>${inline(escapeHtml(lines[0].replace(/^#\s+/, '')))}</h4>`);
      continue;
    }
    // Otherwise a paragraph; single newlines become <br>.
    const html = lines.map((l) => inline(escapeHtml(l))).join('<br>');
    out.push(`<p>${html}</p>`);
  }
  return out.join('');
}

// Plain-text excerpt (for notifications / email subject fallbacks).
function plainExcerpt(md, max = 140) {
  const t = String(md || '').replace(/[#*>_-]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\s+/g, ' ').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}

// Email-friendly plain text — keeps paragraphs + bullets so textEmailToHtml can
// render them, drops bold/italic markers, and renders a link as "text (url)".
function toEmailText(md) {
  return String(md == null ? '' : md)
    .replace(/\r\n/g, '\n')
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, t, u) => (safeUrl(u) ? `${t} (${u})` : t))
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1$2')
    .replace(/^#\s+/gm, '')
    .trim();
}

const api = { renderBoardMarkup, plainExcerpt, toEmailText, escapeHtml, safeUrl };
if (typeof module !== 'undefined' && module.exports) module.exports = api;
if (typeof window !== 'undefined') window.BoardMarkup = api;
